
import React from 'react';
import { Property, AgentProfile } from '../types';
import { MapPin, Bed, Bath, ChevronRight } from 'lucide-react';

interface PropertyCompactRowProps {
  property: Property;
  agent: AgentProfile;
  onDetail: (p: Property) => void;
}

export const PropertyCompactRow: React.FC<PropertyCompactRowProps> = ({ property, agent, onDetail }) => {
  const priceFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: property.currency || 'USD',
    maximumFractionDigits: 0,
  });

  const getPriceColorClass = (category: string) => {
    switch (category) {
      case 'Sale': return 'text-blue-600 dark:text-blue-400';
      case 'Rent': return 'text-green-600 dark:text-green-400';
      case 'Short Let': return 'text-orange-600 dark:text-orange-400';
      case 'JV': return 'text-purple-600 dark:text-purple-400';
      default: return 'text-gray-900 dark:text-white';
    }
  };

  return (
    <div 
      className="flex items-center gap-4 p-3 bg-white dark:bg-black border-b border-gray-100 dark:border-zinc-900 cursor-pointer active:bg-gray-50 dark:active:bg-zinc-900 transition-colors"
      onClick={() => onDetail(property)}
    >
      {/* 35% Width Image Container */}
      <div className="w-[35%] aspect-[4/3] rounded-xl overflow-hidden relative shrink-0 shadow-sm border border-gray-100 dark:border-zinc-800">
        <img 
          src={property.images[0]} 
          alt={property.title} 
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute top-1.5 left-1.5">
           <span className={`px-1.5 py-0.5 rounded text-[7px] font-black uppercase tracking-widest text-white ${
             property.category === 'Sale' ? 'bg-blue-600' : 
             property.category === 'Rent' ? 'bg-green-600' : 
             'bg-amber-600'
           }`}>
             {property.category}
           </span>
        </div>
      </div>

      {/* 65% Width Content Container */}
      <div className="w-[65%] flex flex-col justify-center min-w-0 pr-2">
        <div className="flex items-center justify-between mb-0.5">
           <p className={`text-base font-black tracking-tighter ${getPriceColorClass(property.category)}`}>
             {priceFormatter.format(property.price)}
           </p>
           <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
        </div>
        
        <h3 className="text-xs font-black text-gray-900 dark:text-white truncate tracking-tight mb-1">
          {property.title}
        </h3>
        
        <div className="flex items-center gap-1 text-gray-400 mb-2">
           <MapPin className="w-3 h-3 flex-shrink-0" />
           <span className="text-[10px] font-bold truncate leading-none">{property.location}</span>
        </div>

        <div className="flex items-center gap-3">
           <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-gray-500 dark:text-zinc-500">
              <Bed className="w-3 h-3" /> {property.bedrooms || 0}
           </div>
           <div className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-gray-500 dark:text-zinc-500">
              <Bath className="w-3 h-3" /> {property.bathrooms || 0}
           </div>
           <div className="px-1.5 py-0.5 bg-gray-50 dark:bg-zinc-900 rounded-md text-[8px] font-black uppercase text-gray-400 truncate">
             {property.type}
           </div>
        </div>
      </div>
    </div>
  );
};
